/* tslint-disable */
import * as acorn from 'acorn-jsx';
import * as React from 'react';
import { Component, Fragment } from 'react';
import ATTRIBUTES from '../constants/attributeNames';
import { canHaveChildren, canHaveWhitespace } from '../constants/specialTags';
import { randomHash } from '../helpers/hash';
import parseStyle from '../helpers/parseStyle';
import resolvePath from '../helpers/resolvePath';

const eventsMap: any = {
  onclick: 'onClick',
  onchange: 'onChange',
  onkeyup: 'onKeyUp',
  onkeydown: 'onKeyDown',
  onload: 'onLoad',
  onmouseover: 'onMouseOver',
  onmouseout: 'onMouseOut',
  onmouseup: 'onMouseUp',
  onmousedown: 'onMouseDown',
  onmouseenter: 'onMouseEnter',
  onmouseleave: 'onMouseLeave',
  onfocus: 'onFocus',
  onkeypress: 'onKeyPress',
  onscroll: 'onScroll',
  onsubmit: 'onSubmit'
};

const parserOptions = { plugins: { jsx: true } };

/* eslint-disable consistent-return */
export default class JsxParser extends Component<any, any> {
  static displayName = 'JsxParser';

  static defaultProps = {
    allowUnknownElements: true,
    bindings: {},
    blacklistedTags: ['script'],
    components: {},
    componentsOnly: false,
    disableFragments: false,
    disableKeyGeneration: false,
    jsx: '',
    onError: () => {},
    showWarnings: false,
    renderInWrapper: true
  };

  parseJSX: any = (rawJSX: any) => {
    const wrappedJsx = `<root>${rawJSX}</root>`;
    let parsed: any = [];
    try {
      parsed = new acorn.Parser(parserOptions, wrappedJsx).parse();
      parsed = parsed.body[0].expression.children || [];
    } catch (error) {
      // eslint-disable-next-line no-console
      if (this.props.showWarnings) console.warn(error);
      if (this.props.onError) this.props.onError(error);
      return [];
    }
    return parsed.map(this.parseExpression).filter(Boolean);
  };

  parseExpression: any = (expression: any) => {
    switch (expression.type) {
      case 'JSXElement':
        return this.parseElement(expression);
      case 'JSXText': {
        const key = this.props.disableKeyGeneration ? undefined : randomHash();
        return this.props.disableFragments ? (
          expression.value
        ) : (
          <Fragment key={key}>{expression.value}</Fragment>
        );
      }
      case 'JSXAttribute':
        if (expression.value === null) return true;
        return this.parseExpression(expression.value);
      case 'ConditionalExpression':
        return this.parseExpression(expression.test)
          ? this.parseExpression(expression.consequent)
          : this.parseExpression(expression.alternate);
      case 'ArrayExpression':
        return expression.elements.map(this.parseExpression);
      case 'ObjectExpression': {
        const object: any = {};
        expression.properties.forEach((prop: any) => {
          object[prop.key.name || prop.key.value] = this.parseExpression(
            prop.value
          );
        });
        return object;
      }
      case 'Identifier':
        return (this.props.bindings || {})[expression.name];
      case 'JSXExpressionContainer':
        return this.parseExpression(expression.expression);
      case 'Literal':
        return expression.value;
      case 'MemberExpression':
        return (this.parseExpression(expression.object) || {})[
          expression.property.name
        ];
      case 'CallExpression': {
        const parsedCallee = this.parseExpression(expression.callee);
        if (parsedCallee === undefined) {
          this.props.onError(
            new Error(
              `The expression '${expression.callee}' could not be resolved, resulting in an undefined return value.`
            )
          );
          return undefined;
        }
        break;
      }
      case 'LogicalExpression': {
        const left = this.parseExpression(expression.left);
        if (expression.operator === '||' && left) return true;
        if (
          (expression.operator === '&&' && left) ||
          (expression.operator === '||' && !left)
        ) {
          return this.parseExpression(expression.right);
        }
        return false;
      }
      case 'BinaryExpression':
        /* eslint-disable eqeqeq,max-len */
        switch (expression.operator) {
          case '+':
            return (
              this.parseExpression(expression.left) +
              this.parseExpression(expression.right)
            );
          case '-':
            return (
              this.parseExpression(expression.left) -
              this.parseExpression(expression.right)
            );
          case '*':
            return (
              this.parseExpression(expression.left) *
              this.parseExpression(expression.right)
            );
          case '/':
            return (
              this.parseExpression(expression.left) /
              this.parseExpression(expression.right)
            );
          case '==':
            return (
              this.parseExpression(expression.left) ==
              this.parseExpression(expression.right)
            ).toString();
          case '!=':
            return (
              this.parseExpression(expression.left) !=
              this.parseExpression(expression.right)
            ).toString();
          case '===':
            return (
              this.parseExpression(expression.left) ===
              this.parseExpression(expression.right)
            ).toString();
          case '!==':
            return (
              this.parseExpression(expression.left) !==
              this.parseExpression(expression.right)
            ).toString();
          default:
          /* eslint-enable eqeqeq,max-len */
        }
        break;
      case 'UnaryExpression':
        switch (expression.operator) {
          case '+':
            return expression.argument.value;
          case '-':
            return -1 * expression.argument.value;
          case '!':
            return (!expression.argument.value).toString();
          default:
        }
        break;
      default:
    }
  };

  parseName: any = (element: any) => {
    switch (element.type) {
      case 'JSXIdentifier':
        return element.name;
      case 'JSXMemberExpression':
        return `${this.parseName(element.object)}.${this.parseName(
          element.property
        )}`;
      default:
    }
  };

  parseElement: any = (element: any) => {
    const {
      allowUnknownElements,
      components = {},
      componentsOnly,
      onError
    } = this.props;
    Object.keys(components).forEach(key => {
      components[key.toLowerCase()] = components[key];
    });
    const camelize = (string: string) =>
      string.replace(/-([a-z])/gi, (s, group) => group.toUpperCase());

    const { children: childNodes = [], openingElement } = element;
    /* eslint-disable */
    const { attributes = [] } = openingElement;
    attributes.forEach((attr: any) => {
      if (
        attr.value &&
        attr.value.value &&
        ((attr.value.value.startsWith('[') && attr.value.value.endsWith(']')) ||
          (attr.value.value.startsWith('{') && attr.value.value.endsWith('}')))
      ) {
        try {
          attr.value.value = JSON.parse(attr.value.value);
        } catch {
          console.log('value starts with [] or {} but not json');
        }
      }
      if (
        attr.name &&
        attr.name.name &&
        !attr.name.name.startsWith('aria-') &&
        !attr.name.name.startsWith('data-') &&
        attr.name.name.includes('-')
      ) {
        attr.name.name = camelize(attr.name.name);
      }
    });
    /* eslint-disable */

    const buildDynamicAttr = (name: string, value: string) => {
      return {
        type: 'JSXAttribute',
        start: 1915,
        end: 1932,
        name: { type: 'JSXIdentifier', start: 1915, end: 1922, name },
        value: {
          type: 'JSXExpressionContainer',
          start: 1923,
          end: 1932,
          expression: {
            type: 'Identifier',
            start: 1924,
            end: 1931,
            name: value
          }
        }
      };
    };
    const name = this.parseName(openingElement.name);
    if (!name) {
      onError(
        new Error(
          `The <${openingElement.name}> tag could not be parsed, and will not be rendered.`
        )
      );
      return undefined;
    }

    const blacklistedTags = (this.props.blacklistedTags || [])
      .map((tag: any) => tag.trim().toLowerCase())
      .filter(Boolean);

    if (/^(html|head|body)$/i.test(name))
      return childNodes.map((c: any) => this.parseElement(c));
    const tagName = name.trim().toLowerCase();
    if (blacklistedTags.indexOf(tagName) !== -1) {
      onError(
        new Error(`The tag <${name}> is blacklisted, and will not be rendered.`)
      );
      return undefined;
    }

    if (!resolvePath(components, name)) {
      if (componentsOnly) {
        onError(
          new Error(
            `The componenet <${name}> is unrecognized, and will not be rendered.`
          )
        );
        return undefined;
      }

      if (
        !allowUnknownElements &&
        Object.prototype.toString.call(document.createElement(name)) ===
          '[object HTMLUnknownElement]'
      ) {
        onError(
          new Error(
            `The tag <${name}> is unrecognized in this browser, and will not be rendered.`
          )
        );
        return undefined;
      }
    }
    if (
      Object.keys(components).some(
        key => key.toLowerCase() === name.toLowerCase()
      )
    ) {
      attributes.push(buildDynamicAttr('history', 'history'));
      attributes.push(buildDynamicAttr('qsparams', 'qsparamsval'));
      attributes.push(buildDynamicAttr('userInfo', 'userInfoVal'));
      attributes.push(buildDynamicAttr('childNavigations', 'childNavigations'));
      attributes.push(buildDynamicAttr('callLetter', 'callLetter'));
    }

    let children;
    const component = resolvePath(components, name);
    if (component || canHaveChildren(name)) {
      children = childNodes.map(this.parseExpression);
      if (!component && !canHaveWhitespace(name)) {
        children = children.filter(
          (child: any) => typeof child !== 'string' || !/^\s*$/.test(child)
        );
      }

      if (children.length === 0) {
        children = undefined;
      } else if (children.length === 1) {
        [children] = children;
      }
    }

    const props: any = {
      key: this.props.disableKeyGeneration ? undefined : randomHash()
    };
    attributes.forEach((expr: any) => {
      if (expr.type === 'JSXAttribute') {
        const rawName = expr.name.name;
        const attributeName: any = ATTRIBUTES[rawName] || rawName;
        // if the value is null, this is an implicitly "true" prop, such as readOnly
        const value = this.parseExpression(expr);
        if (value === 'true' || value === 'false') {
          props[attributeName] = value === 'true';
        } else {
          props[attributeName] = value;
        }
      } else if (
        (expr.type === 'JSXSpreadAttribute' &&
          expr.argument.type === 'Identifier') ||
        expr.argument.type === 'MemberExpression'
      ) {
        const value = this.parseExpression(expr.argument);
        if (typeof value === 'object') {
          Object.keys(value).forEach(rawName => {
            const attributeName = ATTRIBUTES[rawName] || rawName;
            props[attributeName] = value[rawName];
          });
        }
      }
    });

    if (typeof props.style === 'string') {
      props.style = parseStyle(props.style);
    }

    if (name.toLowerCase() === 'option') {
      const newProps = { ...props };
      Object.keys(eventsMap).forEach(k => {
        if (newProps[k]) {
          // eslint-disable-next-line
          newProps[eventsMap[k]] = () => {
            // eslint-disable-next-line
            eval(newProps[k]);
          };
        }
      });

      return React.createElement(
        component || name.toLowerCase(),
        newProps,
        children.props.children
      );
    }
    if (children) props.children = children;

    const newProps = { ...props };
    Object.keys(eventsMap).forEach(k => {
      if (newProps[k]) {
        // eslint-disable-next-line
        newProps[eventsMap[k]] = () => {
          // eslint-disable-next-line
          eval(newProps[k]);
        };
      }
    });
    return React.createElement(component || name.toLowerCase(), newProps);
  };

  render = () => {
    const jsx = (this.props.jsx || '').trim().replace(/<!DOCTYPE([^>]*)>/g, '');
    const parsedChildren = this.parseJSX(jsx);
    return this.props.renderInWrapper ? (
      <div className="jsx-parser">{parsedChildren}</div>
    ) : (
      <>{parsedChildren}</>
    );
  };
}
/* eslint-enable consistent-return */

if (process.env.NODE_ENV !== 'production') {
  /* eslint-disable react/no-unused-prop-types */
  // eslint-disable-next-line global-require,import/no-extraneous-dependencies
  const PropTypes = require('prop-types');
  // @ts-ignore
  JsxParser.propTypes = {
    allowUnknownElements: PropTypes.bool,
    bindings: PropTypes.shape({}),
    blacklistedTags: PropTypes.arrayOf(PropTypes.string),
    components: PropTypes.shape({}),
    componentsOnly: PropTypes.bool,
    disableFragments: PropTypes.bool,
    disableKeyGeneration: PropTypes.bool,
    jsx: PropTypes.string,
    onError: PropTypes.func,
    showWarnings: PropTypes.bool,
    renderInWrapper: PropTypes.bool
  };
}
